import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Smartphone, Eye, EyeOff, Copy, Check, RefreshCw, ShieldOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../services/api';

function generatePassword() {
  const chars = 'abcdefghjkmnpqrstuvwxyz';
  const upper = 'ABCDEFGHJKMNPQRSTUVWXYZ';
  const digits = '23456789';
  const all = chars + upper + digits;
  return (
    upper[Math.floor(Math.random() * upper.length)] +
    chars[Math.floor(Math.random() * chars.length)] +
    digits[Math.floor(Math.random() * digits.length)] +
    Array.from({ length: 5 }, () => all[Math.floor(Math.random() * all.length)]).join('')
  );
}

function defaultUsername(firstName, lastName) {
  if (!firstName || !lastName) return '';
  return `${firstName.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, '')}.${lastName.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, '')}`;
}

export default function EmployeeMobileAccessSection({ employee }) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [result, setResult] = useState(null);
  const [roles, setRoles] = useState([]);

  const DELIVERY_OPTIONS = [
    { value: 'manual', label: t('mobileAccess.deliveryManual'), desc: t('mobileAccess.deliveryManualDesc') },
    { value: 'email',  label: t('mobileAccess.deliveryEmail'),  desc: t('mobileAccess.deliveryEmailDesc') },
    { value: 'qr',     label: t('mobileAccess.deliveryQR'),     desc: t('mobileAccess.deliveryQRDesc') },
  ];

  const [form, setForm] = useState({ username: '', password: '', delivery: 'manual', role_ids: [] });

  useEffect(() => {
    api.get('v1/users/roles').then(res => setRoles(Array.isArray(res.data) ? res.data : [])).catch(() => {});
  }, []);

  const isActive = employee?.mobile_access_enabled;
  const linkedUsername = employee?.mobile_user_username;

  useEffect(() => {
    if (dialogOpen) {
      setResult(null);
      setForm({
        username: defaultUsername(employee?.first_name, employee?.last_name),
        password: generatePassword(),
        delivery: 'manual',
        role_ids: [],
      });
    }
  }, [dialogOpen, employee]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleActivate = async () => {
    if (!form.username.trim() || !form.password.trim()) {
      toast.error(t('common.error'), { description: t('mobileAccess.toasts.usernamePasswordRequired') });
      return;
    }
    setSaving(true);
    try {
      const res = await api.post(`v1/employees/${employee.id}/activate-mobile/`, {
        username: form.username.trim(),
        temp_password: form.password,
        role_ids: form.role_ids.length > 0 ? form.role_ids : null,
        delivery_method: form.delivery,
      });
      setResult({ ...res.data, password: form.password });
      toast.success(t('mobileAccess.toasts.accessActivated'), { description: t('mobileAccess.toasts.accountCreated', { username: res.data.username }) });
    } catch (err) {
      toast.error(t('common.error'), { description: err?.response?.data?.detail || t('mobileAccess.toasts.activateError') });
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm(t('mobileAccess.toasts.revokeConfirm'))) return;
    try {
      await api.delete(`v1/employees/${employee.id}/deactivate-mobile/`);
      toast.success(t('mobileAccess.toasts.accessRevoked'), { description: t('mobileAccess.toasts.accessRevokedDesc') });
      window.location.reload();
    } catch (err) {
      toast.error(t('common.error'), { description: err?.response?.data?.detail || t('mobileAccess.toasts.revokeError') });
    }
  };

  if (!employee?.id) return null;

  return (
    <>
      <Card className="border-primary/30">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Smartphone className="h-4 w-4 text-muted-foreground" />
            {t('mobileAccess.title')}
            {isActive ? (
              <Badge className="bg-green-600 ml-2">{t('mobileAccess.active')}</Badge>
            ) : (
              <Badge variant="secondary" className="ml-2">{t('mobileAccess.inactive')}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isActive ? (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {t('mobileAccess.linkedAccount')} <span className="font-medium text-foreground">@{linkedUsername || '—'}</span>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
                  <RefreshCw className="h-3 w-3 mr-1" /> {t('mobileAccess.resetPassword')}
                </Button>
                <Button variant="outline" size="sm" className="text-red-400 border-red-900/40 hover:bg-red-950/20" onClick={handleDeactivate}>
                  <ShieldOff className="h-3 w-3 mr-1" /> {t('mobileAccess.revoke')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{t('mobileAccess.noAccess')}</p>
              <Button size="sm" onClick={() => setDialogOpen(true)}>
                <Smartphone className="h-3 w-3 mr-1" /> {t('mobileAccess.enable')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(v) => !saving && setDialogOpen(v)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-4 w-4" />
              {isActive ? t('mobileAccess.dialogTitleReset') : t('mobileAccess.dialogTitleActivate')}
            </DialogTitle>
          </DialogHeader>

          {!result ? (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label>{t('mobileAccess.username')} *</Label>
                <Input value={form.username} onChange={e => set('username', e.target.value)} placeholder={t('mobileAccess.usernamePlaceholder')} autoCapitalize="none" />
              </div>

              <div className="space-y-1">
                <Label>{t('mobileAccess.tempPassword')} *</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      value={form.password}
                      onChange={e => set('password', e.target.value)}
                      type={showPwd ? 'text' : 'password'}
                    />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowPwd(v => !v)}>
                      {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <Button variant="outline" size="icon" onClick={() => set('password', generatePassword())}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {!isActive && roles?.length > 0 && (
                <div className="space-y-2">
                  <Label>{t('mobileAccess.role')}</Label>
                  <div className="flex flex-wrap gap-2">
                    {roles.map(r => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => set('role_ids', form.role_ids.includes(r.id) ? form.role_ids.filter(id => id !== r.id) : [...form.role_ids, r.id])}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${form.role_ids.includes(r.id) ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary'}`}
                      >
                        {r.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>{t('mobileAccess.deliveryMode')}</Label>
                <div className="space-y-2">
                  {DELIVERY_OPTIONS.map(opt => (
                    <label key={opt.value} className="flex items-start gap-3 cursor-pointer p-2 rounded border hover:bg-accent">
                      <input type="radio" name="delivery" value={opt.value} checked={form.delivery === opt.value} onChange={() => set('delivery', opt.value)} className="mt-0.5" />
                      <div>
                        <span className="font-medium text-sm">{opt.label}</span>
                        <p className="text-xs text-muted-foreground">{opt.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>{t('mobileAccess.cancel')}</Button>
                <Button onClick={handleActivate} disabled={saving}>
                  {saving ? t('mobileAccess.activating') : t('mobileAccess.activate')}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-950/20 border border-green-900/40 rounded-lg p-4 space-y-3">
                <p className="text-sm font-semibold text-green-400">✓ {t('mobileAccess.successTitle')}</p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{t('mobileAccess.usernameLabel')}</span>
                    <span className="font-mono font-bold">{result.username}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{t('mobileAccess.passwordLabel')}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold tracking-wider">{result.password}</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(`${result.username} / ${result.password}`)}>
                        {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                      </Button>
                    </div>
                  </div>
                </div>
                {result.email_sent && (
                  <p className="text-xs text-green-400">📧 {t('mobileAccess.emailSent')}</p>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{t('mobileAccess.loginInstruction')}</p>
              <DialogFooter>
                <Button onClick={() => { setDialogOpen(false); window.location.reload(); }}>{t('mobileAccess.close')}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
